// /ugc-finder — UGC Finder. Discover TikTok + Instagram creators to partner
// with (outreach / UGC), ranked by relevance-to-brand + engagement + reach,
// and save them into a lightweight outreach library (prospect → contacted →
// partnered). Backend: routes/creators.py (/api/ugc/*), ScrapeCreators-backed.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayoutV2 from '@/components/feature/AppLayoutV2';
import { useBrand } from '@/hooks/useBrand';
import { useCredits } from '@/hooks/useCredits';
import {
  suggestUgcTerms,
  discoverCreators,
  listSavedCreators,
  saveCreator,
  updateCreator,
  deleteCreator,
  listProducts,
  type DiscoveredCreator,
  type SavedCreator,
  type UserProduct,
} from '@/lib/api';

type Platform = 'tiktok' | 'instagram';
type Tier = 'all' | 'nano' | 'micro' | 'mid' | 'macro';
type StatusFilter = 'all' | 'prospect' | 'contacted' | 'partnered';

const TIER_BOUNDS: Record<Exclude<Tier, 'all'>, [number, number]> = {
  nano: [0, 10_000],
  micro: [10_000, 100_000],
  mid: [100_000, 500_000],
  macro: [500_000, 0], // 0 max = no upper bound (backend treats mx<=0 as unbounded)
};

const TIER_LABEL: Record<Tier, string> = {
  all: 'All sizes',
  nano: 'Nano (<10K)',
  micro: 'Micro (10K–100K)',
  mid: 'Mid (100K–500K)',
  macro: 'Macro (500K+)',
};

const PLATFORM_META: Record<Platform, { label: string; icon: string; color: string }> = {
  tiktok: { label: 'TikTok', icon: 'ri-tiktok-fill', color: '#000000' },
  instagram: { label: 'Instagram', icon: 'ri-instagram-line', color: '#E1306C' },
};

const STATUS_META: Record<'prospect' | 'contacted' | 'partnered', { label: string; cls: string }> = {
  prospect: { label: 'Prospect', cls: 'bg-surface-dark/50 text-dark-muted' },
  contacted: { label: 'Contacted', cls: 'bg-amber-50 text-amber-700' },
  partnered: { label: 'Partnered', cls: 'bg-emerald-50 text-emerald-700' },
};

function fmtFollowers(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(n >= 10_000_000 ? 0 : 1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(n >= 10_000 ? 0 : 1)}K`;
  return String(n);
}

function keyOf(platform: string, handle: string): string {
  return `${platform}:${handle.toLowerCase()}`;
}

// Discovery modes — all three feed the SAME /api/ugc/discover; they just seed
// different keywords/hashtags. 'mention'/'product' are text-match approximations
// (no true @tag / shopping-tag data is available to scrape — see the docs).
type DiscoverMode = 'niche' | 'mention' | 'product';

const MODE_LABEL: Record<DiscoverMode, string> = {
  niche: 'By niche',
  mention: 'Mentioning my brand',
  product: 'By product',
};

function normalizeTag(s: string): string {
  return (s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
}

function handleFromUrl(url?: string | null): string {
  if (!url) return '';
  try {
    const host = new URL(url.startsWith('http') ? url : `https://${url}`).hostname.replace(/^www\./, '');
    return normalizeTag(host.split('.')[0]);
  } catch {
    return '';
  }
}

export default function UgcFinderPage() {
  const { activeBrand } = useBrand();
  const { planTier } = useCredits();
  const navigate = useNavigate();
  // UGC Finder is a Pro+ feature (per-search ScrapeCreators cost). Non-premium
  // users see an upsell instead of the discover/saved surface. Backend enforces
  // the same gate (routes/creators.py → require_pro) as a backstop.
  const isPremium = planTier === 'pro' || planTier === 'business';
  const [tab, setTab] = useState<'discover' | 'saved'>('discover');

  // ── discover state ──
  const [mode, setMode] = useState<DiscoverMode>('niche');
  const [products, setProducts] = useState<UserProduct[]>([]);
  const [selectedProductId, setSelectedProductId] = useState('');
  // The niche-mode seed (from suggest-terms) — cached so switching back to
  // "By niche" restores it instead of re-hitting the API.
  const nicheSeedRef = useRef<{ keywords: string[]; hashtags: string[] }>({ keywords: [], hashtags: [] });
  const [keywords, setKeywords] = useState<string[]>([]);
  const [hashtags, setHashtags] = useState<string[]>([]);
  const [kwInput, setKwInput] = useState('');
  const [tagInput, setTagInput] = useState('');
  const [platforms, setPlatforms] = useState<Platform[]>(['tiktok', 'instagram']);
  const [tier, setTier] = useState<Tier>('all');
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [results, setResults] = useState<DiscoveredCreator[]>([]);
  const [searching, setSearching] = useState(false);
  const [searched, setSearched] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [seeding, setSeeding] = useState(false);

  // ── saved state ──
  const [saved, setSaved] = useState<SavedCreator[]>([]);
  const [savedLoading, setSavedLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

  const savedKeys = useMemo(
    () => new Set(saved.map((c) => keyOf(c.platform, c.handle))),
    [saved],
  );

  // Load saved library + auto-seed search terms from the brand on mount.
  const loadSaved = useCallback(async () => {
    setSavedLoading(true);
    try {
      setSaved(await listSavedCreators());
    } catch {
      /* non-fatal — empty library */
    } finally {
      setSavedLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadSaved();
  }, [loadSaved, activeBrand?.id]);

  useEffect(() => {
    // Seed keyword/hashtag suggestions from the brand's products once per brand.
    // Cache the seed so "By niche" can restore it after switching modes; only
    // apply to the live chips when we're actually in niche mode.
    let cancelled = false;
    (async () => {
      setSeeding(true);
      try {
        const t = await suggestUgcTerms();
        if (cancelled) return;
        nicheSeedRef.current = { keywords: t.keywords || [], hashtags: t.hashtags || [] };
        setKeywords((prev) => (mode === 'niche' && prev.length === 0 ? t.keywords || [] : prev));
        setHashtags((prev) => (mode === 'niche' && prev.length === 0 ? t.hashtags || [] : prev));
      } catch {
        /* no products yet — user types their own */
      } finally {
        if (!cancelled) setSeeding(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBrand?.id]);

  useEffect(() => {
    // Products for the "By product" picker.
    let cancelled = false;
    (async () => {
      try {
        const p = await listProducts();
        if (!cancelled) setProducts(p);
      } catch {
        /* non-fatal — product mode just shows an empty picker */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeBrand?.id]);

  const addChip = (v: string, list: string[], set: (x: string[]) => void, clear: () => void) => {
    const t = v.trim().replace(/^#/, '');
    if (t && !list.some((x) => x.toLowerCase() === t.toLowerCase())) set([...list, t]);
    clear();
  };

  const togglePlatform = (p: Platform) =>
    setPlatforms((prev) => (prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p]));

  const switchMode = (m: DiscoverMode) => {
    setMode(m);
    setResults([]);
    setSearched(false);
    setError(null);
    if (m === 'niche') {
      setKeywords(nicheSeedRef.current.keywords);
      setHashtags(nicheSeedRef.current.hashtags);
    } else if (m === 'mention') {
      const name = (activeBrand?.name || '').trim();
      setKeywords(name ? [name] : []);
      setHashtags(
        Array.from(new Set([normalizeTag(name), handleFromUrl(activeBrand?.sourceUrl)].filter(Boolean))),
      );
    } else {
      // product — wait for a pick
      setSelectedProductId('');
      setKeywords([]);
      setHashtags([]);
    }
  };

  const selectProduct = (id: string) => {
    setSelectedProductId(id);
    const p = products.find((x) => x.id === id);
    if (!p) {
      setKeywords([]);
      setHashtags([]);
      return;
    }
    const name = (p.name || '').trim();
    setKeywords(Array.from(new Set([name, ...(p.tags || []).slice(0, 3)].filter(Boolean))));
    setHashtags(name ? [normalizeTag(name)] : []);
  };

  const modeHelp: Record<DiscoverMode, string> = {
    niche: 'Creators in your niche, seeded from your products. Edit the terms below.',
    mention: `Creators mentioning ${activeBrand?.name || 'your brand'} in captions/hashtags (text match — not verified @tags). Add your exact @handle for better hits.`,
    product: 'Pick a product — we search for creators posting about it by name + hashtag.',
  };

  const handleSearch = async () => {
    if (searching) return;
    if (keywords.length === 0 && hashtags.length === 0) {
      setError('Add at least one keyword or hashtag.');
      return;
    }
    if (platforms.length === 0) {
      setError('Pick at least one platform.');
      return;
    }
    setError(null);
    setSearching(true);
    try {
      const filters: Record<string, number | boolean> = {};
      if (tier !== 'all') {
        const [lo, hi] = TIER_BOUNDS[tier];
        filters.min_followers = lo;
        filters.max_followers = hi;
      }
      if (verifiedOnly) filters.verified_only = true;
      const res = await discoverCreators({ keywords, hashtags, platforms, filters });
      setResults(res.creators);
      setSearched(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Search failed');
    } finally {
      setSearching(false);
    }
  };

  const handleSave = async (c: DiscoveredCreator) => {
    // Optimistic: mark saved immediately.
    const optimistic: SavedCreator = {
      id: `tmp-${c.platform}-${c.handle}`,
      platform: c.platform,
      handle: c.handle,
      display_name: c.display_name,
      followers: c.followers,
      bio: c.bio,
      avatar_url: c.avatar_url,
      verified: c.verified,
      external_link: c.external_link,
      contact_email: c.contact_email,
      category: c.category,
      engagement_rate: c.engagement_rate,
      profile_url: c.profile_url,
      niche_tags: [],
      status: 'prospect',
      notes: null,
      created_at: new Date().toISOString(),
    };
    setSaved((prev) => [optimistic, ...prev]);
    try {
      const real = await saveCreator(c);
      setSaved((prev) => [real, ...prev.filter((x) => x.id !== optimistic.id)]);
    } catch {
      setSaved((prev) => prev.filter((x) => x.id !== optimistic.id));
      setError('Could not save creator.');
    }
  };

  const handleStatus = async (id: string, status: 'prospect' | 'contacted' | 'partnered') => {
    setSaved((prev) => prev.map((c) => (c.id === id ? { ...c, status } : c)));
    try {
      await updateCreator(id, { status });
    } catch {
      void loadSaved();
    }
  };

  const handleDelete = async (id: string) => {
    setSaved((prev) => prev.filter((c) => c.id !== id));
    try {
      await deleteCreator(id);
    } catch {
      void loadSaved();
    }
  };

  const filteredSaved =
    statusFilter === 'all' ? saved : saved.filter((c) => c.status === statusFilter);

  // ── Pro gate — non-premium users get an upsell, not the tool ──
  if (!isPremium) {
    return (
      <AppLayoutV2>
        <div className="max-w-[1500px] mx-auto px-6 py-8">
          <div className="max-w-lg mx-auto mt-8 rounded-2xl border border-surface-dark/40 bg-white p-8 text-center">
            <div className="w-14 h-14 rounded-2xl bg-primary/10 text-primary flex items-center justify-center mx-auto mb-4">
              <i className="ri-group-2-line text-2xl" />
            </div>
            <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-primary-light text-primary mb-3">
              Pro feature
            </div>
            <h1 className="text-2xl font-bold text-dark mb-2">UGC Finder</h1>
            <p className="text-sm text-dark-muted mb-6">
              Discover TikTok &amp; Instagram creators matched to your brand, ranked
              by relevance, engagement, and reach — then build an outreach library.
              Available on the Pro plan and above.
            </p>
            <ul className="text-left text-sm text-dark-muted space-y-2 mb-7 max-w-sm mx-auto">
              {[
                'Search creators by niche, brand mention, or product',
                'Filter by follower tier, engagement, and verification',
                'Auto-pull contact emails when you save a creator',
                'Track outreach: prospect → contacted → partnered',
              ].map((f) => (
                <li key={f} className="flex items-start gap-2">
                  <i className="ri-check-line text-primary mt-0.5 flex-shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
            <button
              onClick={() => navigate('/checkout')}
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-primary text-white text-sm font-bold shadow-md shadow-primary/25 hover:bg-primary-hover transition-colors"
            >
              <i className="ri-vip-crown-line" />
              Upgrade to Pro
            </button>
          </div>
        </div>
      </AppLayoutV2>
    );
  }

  return (
    <AppLayoutV2>
      <div className="max-w-[1500px] mx-auto px-6 py-8">
        {/* header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-11 h-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
            <i className="ri-group-2-line text-xl" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-dark">UGC Finder</h1>
            <p className="text-sm text-dark-muted">
              Find TikTok & Instagram creators to partner with — matched to your brand.
            </p>
          </div>
        </div>

        {/* tab switch */}
        <div className="inline-flex rounded-full bg-surface border border-surface-dark/40 p-1 mb-6">
          {(['discover', 'saved'] as const).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-5 py-1.5 rounded-full text-sm font-semibold transition-colors ${
                tab === t ? 'bg-white text-primary shadow-md shadow-primary/15' : 'text-dark-muted'
              }`}
            >
              {t === 'discover' ? 'Discover' : `Saved${saved.length ? ` · ${saved.length}` : ''}`}
            </button>
          ))}
        </div>

        {tab === 'discover' ? (
          <>
            {/* search panel */}
            <div className="rounded-2xl border border-surface-dark/40 bg-white p-5 mb-6">
              {/* mode selector */}
              <div className="flex flex-wrap gap-2 mb-3">
                {(Object.keys(MODE_LABEL) as DiscoverMode[]).map((m) => (
                  <button
                    key={m}
                    onClick={() => switchMode(m)}
                    className={`px-3 py-1.5 rounded-full text-sm font-semibold border transition-colors ${
                      mode === m
                        ? 'border-primary bg-primary-light text-primary'
                        : 'border-surface-dark/40 text-dark-muted'
                    }`}
                  >
                    {MODE_LABEL[m]}
                  </button>
                ))}
              </div>
              <p className="text-xs text-dark-muted mb-4">{modeHelp[mode]}</p>

              {/* product picker (product mode only) */}
              {mode === 'product' && (
                <div className="mb-4">
                  <label className="block text-xs font-bold uppercase tracking-wider text-dark-muted mb-1.5">
                    Product
                  </label>
                  <select
                    value={selectedProductId}
                    onChange={(e) => selectProduct(e.target.value)}
                    className="w-full md:w-1/2 px-3 py-2 rounded-xl text-sm border border-surface-dark/40 bg-white text-dark"
                  >
                    <option value="">
                      {products.length ? 'Choose a product…' : 'No products in this brand yet'}
                    </option>
                    {products.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name || 'Untitled product'}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="grid md:grid-cols-2 gap-4">
                <ChipField
                  label="Niche keywords"
                  placeholder="e.g. handmade pottery"
                  value={kwInput}
                  onChange={setKwInput}
                  chips={keywords}
                  onAdd={() => addChip(kwInput, keywords, setKeywords, () => setKwInput(''))}
                  onRemove={(v) => setKeywords(keywords.filter((x) => x !== v))}
                />
                <ChipField
                  label="Hashtags"
                  placeholder="e.g. potterytok"
                  prefix="#"
                  value={tagInput}
                  onChange={setTagInput}
                  chips={hashtags}
                  onAdd={() => addChip(tagInput, hashtags, setHashtags, () => setTagInput(''))}
                  onRemove={(v) => setHashtags(hashtags.filter((x) => x !== v))}
                />
              </div>

              <div className="flex flex-wrap items-center gap-3 mt-4">
                {/* platforms */}
                <div className="flex gap-2">
                  {(Object.keys(PLATFORM_META) as Platform[]).map((p) => {
                    const on = platforms.includes(p);
                    const m = PLATFORM_META[p];
                    return (
                      <button
                        key={p}
                        onClick={() => togglePlatform(p)}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold border transition-colors ${
                          on
                            ? 'border-primary bg-primary-light text-primary'
                            : 'border-surface-dark/40 text-dark-muted'
                        }`}
                      >
                        <i className={m.icon} style={{ color: on ? undefined : m.color }} />
                        {m.label}
                      </button>
                    );
                  })}
                </div>

                {/* tier */}
                <select
                  value={tier}
                  onChange={(e) => setTier(e.target.value as Tier)}
                  className="px-3 py-1.5 rounded-full text-sm border border-surface-dark/40 bg-white text-dark"
                >
                  {(Object.keys(TIER_LABEL) as Tier[]).map((t) => (
                    <option key={t} value={t}>
                      {TIER_LABEL[t]}
                    </option>
                  ))}
                </select>

                {/* verified */}
                <label className="flex items-center gap-1.5 text-sm text-dark-muted cursor-pointer">
                  <input
                    type="checkbox"
                    checked={verifiedOnly}
                    onChange={(e) => setVerifiedOnly(e.target.checked)}
                  />
                  Verified only
                </label>

                <div className="flex-1" />

                <button
                  onClick={() => void handleSearch()}
                  disabled={searching}
                  className="px-6 py-2 rounded-full bg-primary text-white text-sm font-bold hover:bg-primary-hover disabled:opacity-60 shadow-md shadow-primary/20"
                >
                  {searching ? 'Searching…' : 'Find creators'}
                </button>
              </div>
              {seeding && (
                <p className="text-xs text-dark-muted mt-3">Loading brand-based suggestions…</p>
              )}
              {error && <p className="text-sm text-red-600 mt-3">{error}</p>}
            </div>

            {/* results */}
            {searching ? (
              <p className="text-sm text-dark-muted">Searching TikTok + Instagram…</p>
            ) : searched && results.length === 0 ? (
              <div className="text-center py-16 text-dark-muted">
                <i className="ri-search-eye-line text-4xl mb-2 block opacity-50" />
                No creators matched. Try broader keywords or fewer filters.
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {results.map((c) => (
                  <CreatorCard
                    key={keyOf(c.platform, c.handle)}
                    c={c}
                    saved={savedKeys.has(keyOf(c.platform, c.handle))}
                    onSave={() => void handleSave(c)}
                  />
                ))}
              </div>
            )}
          </>
        ) : (
          <>
            {/* saved status filter */}
            <div className="flex gap-2 mb-4">
              {(['all', 'prospect', 'contacted', 'partnered'] as StatusFilter[]).map((s) => (
                <button
                  key={s}
                  onClick={() => setStatusFilter(s)}
                  className={`px-3 py-1.5 rounded-full text-sm font-semibold border transition-colors ${
                    statusFilter === s
                      ? 'border-primary bg-primary-light text-primary'
                      : 'border-surface-dark/40 text-dark-muted'
                  }`}
                >
                  {s === 'all' ? 'All' : STATUS_META[s].label}
                </button>
              ))}
            </div>

            {savedLoading ? (
              <p className="text-sm text-dark-muted">Loading…</p>
            ) : filteredSaved.length === 0 ? (
              <div className="text-center py-16 text-dark-muted">
                <i className="ri-bookmark-line text-4xl mb-2 block opacity-50" />
                No saved creators yet. Discover some and hit Save.
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredSaved.map((c) => (
                  <SavedCard
                    key={c.id}
                    c={c}
                    onStatus={(s) => void handleStatus(c.id, s)}
                    onDelete={() => void handleDelete(c.id)}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </AppLayoutV2>
  );
}

// ── sub-components ──────────────────────────────────────────────────────
function ChipField(props: {
  label: string;
  placeholder: string;
  prefix?: string;
  value: string;
  onChange: (v: string) => void;
  chips: string[];
  onAdd: () => void;
  onRemove: (v: string) => void;
}) {
  return (
    <div>
      <label className="block text-xs font-bold uppercase tracking-wider text-dark-muted mb-1.5">
        {props.label}
      </label>
      <div className="flex flex-wrap gap-1.5 items-center rounded-xl border border-surface-dark/40 bg-white px-2 py-2 min-h-[44px]">
        {props.chips.map((chip) => (
          <span
            key={chip}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary-light text-primary text-xs font-semibold"
          >
            {props.prefix}
            {chip}
            <button onClick={() => props.onRemove(chip)} className="hover:text-primary-hover">
              <i className="ri-close-line" />
            </button>
          </span>
        ))}
        <input
          value={props.value}
          onChange={(e) => props.onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              props.onAdd();
            }
          }}
          placeholder={props.chips.length === 0 ? props.placeholder : ''}
          className="flex-1 min-w-[80px] text-sm outline-none bg-transparent"
        />
      </div>
    </div>
  );
}

function Avatar({ url, name, verified }: { url: string | null; name: string; verified: boolean }) {
  return (
    <div className="relative flex-shrink-0">
      {url ? (
        <img src={url} alt={name} className="w-12 h-12 rounded-full object-cover" loading="lazy" />
      ) : (
        <div className="w-12 h-12 rounded-full bg-surface-dark/40 flex items-center justify-center text-dark-muted font-bold">
          {(name || '?').charAt(0).toUpperCase()}
        </div>
      )}
      {verified && (
        <i className="ri-verified-badge-fill absolute -bottom-0.5 -right-0.5 text-primary text-sm bg-white rounded-full" />
      )}
    </div>
  );
}

function CreatorCard({ c, saved, onSave }: { c: DiscoveredCreator; saved: boolean; onSave: () => void }) {
  const m = PLATFORM_META[c.platform];
  return (
    <div className="rounded-2xl border border-surface-dark/40 bg-white p-4 flex flex-col gap-3">
      <div className="flex items-start gap-3">
        <Avatar url={c.avatar_url} name={c.display_name} verified={c.verified} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className="font-bold text-dark truncate">{c.display_name}</span>
            <i className={m.icon} style={{ color: m.color }} title={m.label} />
          </div>
          <a
            href={c.profile_url}
            target="_blank"
            rel="noreferrer"
            className="text-sm text-dark-muted hover:text-primary truncate block"
          >
            @{c.handle}
          </a>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 text-xs">
        <span className="px-2 py-0.5 rounded-full bg-surface text-dark font-semibold">
          {fmtFollowers(c.followers)} followers
        </span>
        {c.engagement_rate != null && (
          <span className="px-2 py-0.5 rounded-full bg-surface text-dark font-semibold">
            {(c.engagement_rate * 100).toFixed(1)}% eng
          </span>
        )}
        {c.category && (
          <span className="px-2 py-0.5 rounded-full bg-surface text-dark-muted">{c.category}</span>
        )}
      </div>

      {c.bio && <p className="text-xs text-dark-muted line-clamp-2 min-h-[2em]">{c.bio}</p>}

      <div className="flex items-center gap-2 mt-auto pt-1">
        <button
          onClick={onSave}
          disabled={saved}
          className={`flex-1 px-3 py-1.5 rounded-full text-sm font-bold transition-colors ${
            saved
              ? 'bg-emerald-50 text-emerald-700 cursor-default'
              : 'bg-primary text-white hover:bg-primary-hover'
          }`}
        >
          {saved ? '✓ Saved' : 'Save'}
        </button>
        <a
          href={c.profile_url}
          target="_blank"
          rel="noreferrer"
          className="w-9 h-9 rounded-full border border-surface-dark/40 flex items-center justify-center text-dark-muted hover:text-primary hover:border-primary/40"
          title="Open profile"
        >
          <i className="ri-external-link-line" />
        </a>
      </div>
    </div>
  );
}

function SavedCard(props: {
  c: SavedCreator;
  onStatus: (s: 'prospect' | 'contacted' | 'partnered') => void;
  onDelete: () => void;
}) {
  const { c } = props;
  const m = PLATFORM_META[c.platform];
  return (
    <div className="group rounded-2xl border border-surface-dark/40 bg-white p-4 flex flex-col gap-3">
      <div className="flex items-start gap-3">
        <Avatar url={c.avatar_url} name={c.display_name || c.handle} verified={c.verified} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className="font-bold text-dark truncate">{c.display_name || c.handle}</span>
            <i className={m.icon} style={{ color: m.color }} title={m.label} />
          </div>
          <a
            href={c.profile_url || '#'}
            target="_blank"
            rel="noreferrer"
            className="text-sm text-dark-muted hover:text-primary truncate block"
          >
            @{c.handle}
          </a>
        </div>
        <button
          onClick={props.onDelete}
          className="opacity-0 group-hover:opacity-100 w-7 h-7 rounded-full bg-red-50 text-red-500 hover:bg-red-500 hover:text-white flex items-center justify-center flex-shrink-0"
          title="Remove"
        >
          <i className="ri-delete-bin-line text-sm" />
        </button>
      </div>

      <div className="flex flex-wrap gap-2 text-xs">
        <span className="px-2 py-0.5 rounded-full bg-surface text-dark font-semibold">
          {fmtFollowers(c.followers)} followers
        </span>
        {c.engagement_rate != null && (
          <span className="px-2 py-0.5 rounded-full bg-surface text-dark font-semibold">
            {(c.engagement_rate * 100).toFixed(1)}% eng
          </span>
        )}
      </div>

      {c.contact_email ? (
        <a
          href={`mailto:${c.contact_email}`}
          className="text-xs text-primary hover:underline truncate flex items-center gap-1"
        >
          <i className="ri-mail-line" />
          {c.contact_email}
        </a>
      ) : (
        <span className="text-xs text-dark-muted/70 flex items-center gap-1">
          <i className="ri-mail-line" /> No public email
        </span>
      )}

      <div className="flex items-center gap-2 mt-auto pt-1">
        <select
          value={c.status}
          onChange={(e) => props.onStatus(e.target.value as 'prospect' | 'contacted' | 'partnered')}
          className={`flex-1 px-3 py-1.5 rounded-full text-xs font-bold border-0 cursor-pointer ${STATUS_META[c.status].cls}`}
        >
          <option value="prospect">Prospect</option>
          <option value="contacted">Contacted</option>
          <option value="partnered">Partnered</option>
        </select>
      </div>
    </div>
  );
}
